import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getDefaultRouteForRole } from '@/lib/auth'
import {
  confirmEmail,
  getCurrentUserRole,
  resendEmailConfirmation,
  signInUser,
  signOutUser,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'

export function LoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Link from the confirmation mail: /login?email=…&token=…
  const [searchParams, setSearchParams] = useSearchParams()
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '')
  const [password, setPassword] = useState('')

  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const confirmationStarted = useRef(false)
  const confirmMutation = useMutation({
    mutationFn: confirmEmail,
    onSuccess: () => toast.success('E-Mail-Adresse bestätigt. Du kannst dich jetzt einloggen.'),
    onError: (error) => toast.error(getErrorMessage(error)),
    onSettled: () => setSearchParams({}, { replace: true }),
  })

  useEffect(() => {
    const confirmationEmail = searchParams.get('email')
    const token = searchParams.get('token')
    if (confirmationEmail && token && !confirmationStarted.current) {
      confirmationStarted.current = true
      confirmMutation.mutate({ email: confirmationEmail, token })
    }
  }, [searchParams, confirmMutation])

  const resendMutation = useMutation({
    mutationFn: () => resendEmailConfirmation(email.trim().toLowerCase()),
    onSuccess: () => toast.success('Wir haben dir einen neuen Bestätigungslink geschickt.'),
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const loginMutation = useMutation({
    mutationFn: async () => {
      await signInUser({
        email: email.trim().toLowerCase(),
        password,
      })

      const profile = await getCurrentUserRole()
      if (!profile || !profile.is_active) {
        await signOutUser()
        throw new Error('ACCOUNT_INACTIVE')
      }

      return profile
    },
    onSuccess: async (profile) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      toast.success('Erfolgreich eingeloggt.')
      navigate(getDefaultRouteForRole(profile.role), { replace: true })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  if (profileQuery.data?.is_active) {
    return <Navigate to={getDefaultRouteForRole(profileQuery.data.role)} replace />
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <img src="/motionlab.png" alt="motionLAB Logo" className="h-24 w-auto object-contain" />
          <CardTitle>Motionlab Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              loginMutation.mutate()
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {loginMutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>{getErrorMessage(loginMutation.error)}</AlertDescription>
              </Alert>
            )}

            {loginMutation.error?.message === 'EMAIL_NOT_CONFIRMED' && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={resendMutation.isPending}
                onClick={() => resendMutation.mutate()}
              >
                Bestätigungslink erneut senden
              </Button>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? 'Anmeldung ...' : 'Einloggen'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link className="text-primary underline" to="/passwort-vergessen">
                Passwort vergessen?
              </Link>
            </p>

            <p className="text-center text-sm text-muted-foreground">
              Noch kein Konto?{' '}
              <Link className="text-primary underline" to="/registrieren">
                Jetzt registrieren
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
